import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { CAN_CREATE_PROJECT, STAGE_PRODUCTION } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  UserX, AlertTriangle, CalendarX2, CheckCircle2, LogOut as LogOutIcon, ClipboardCheck,
  Loader2, Building2, Truck, PlusCircle, ArrowRight, Factory
} from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [attention, setAttention] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, a] = await Promise.all([
          api.get("/dashboard/stats"), api.get("/dashboard/recent"), api.get("/dashboard/attention"),
        ]);
        setStats(s.data); setRecent(r.data); setAttention(a.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const OP = [
    { key: "no_pic", label: "Tanpa Penanggung Jawab", icon: UserX, to: "/projects?flag=no_pic", accent: true },
    { key: "overdue", label: "Melewati Target", icon: CalendarX2, to: "/projects?flag=overdue", accent: true },
    { key: "no_report_today", label: "Tanpa Laporan Hari Ini", icon: AlertTriangle, to: "/projects?flag=no_report_today" },
    { key: "ready", label: "Unit Ready", icon: CheckCircle2, to: "/repair-orders?status=Ready" },
    { key: "unit_out", label: "Unit Out", icon: LogOutIcon, to: "/projects?stage=Unit Out" },
    { key: "dr_pending", label: "Daily Report Menunggu", icon: ClipboardCheck, to: "/daily-reports?scope=pending" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Ringkasan operasional perbaikan armada K3.</p>
        </div>
        {user && CAN_CREATE_PROJECT.includes(user.role) && (
          <Button onClick={() => navigate("/projects/new")} data-testid="dashboard-new-project" className="bg-primary hover:bg-red-700">
            <PlusCircle size={16} className="mr-1.5" /> Buat Proyek Baru
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {OP.map((d) => {
          const Icon = d.icon;
          return (
            <Card key={d.key} data-testid={`stat-${d.key}`} onClick={() => navigate(d.to)}
              className={`p-4 cursor-pointer hover:shadow-md transition-shadow border ${d.accent && stats?.[d.key] > 0 ? "border-red-300 bg-red-50" : ""}`}>
              <Icon size={18} className={d.accent && stats?.[d.key] > 0 ? "text-primary" : "text-navy"} />
              <div className="text-2xl font-bold mt-2 font-heading text-navy">{stats?.[d.key] ?? 0}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{d.label}</div>
            </Card>
          );
        })}
      </div>

      <Card className="p-4">
        <h2 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><Factory size={16} className="text-primary" /> Unit Berdasarkan Tahap Produksi</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {STAGE_PRODUCTION.map((s) => (
            <button key={s} data-testid={`stage-count-${s}`} onClick={() => navigate(`/repair-orders?status=${encodeURIComponent(s)}`)}
              className="border rounded-lg p-3 text-left hover:border-primary transition-colors">
              <div className="text-xl font-bold font-heading text-navy">{stats?.by_stage?.[s] ?? 0}</div>
              <div className="text-[11px] text-muted-foreground leading-tight">{s}</div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 flex items-center gap-3 cursor-pointer" data-testid="stat-active_customers" onClick={() => navigate("/customers")}>
          <Building2 className="text-navy" size={22} />
          <div><div className="text-xl font-bold font-heading">{stats?.active_customers ?? 0}</div><div className="text-xs text-muted-foreground">Customer Aktif</div></div>
        </Card>
        <Card className="p-4 flex items-center gap-3 cursor-pointer" data-testid="stat-active_vehicles" onClick={() => navigate("/vehicles")}>
          <Truck className="text-navy" size={22} />
          <div><div className="text-xl font-bold font-heading">{stats?.active_vehicles ?? 0}</div><div className="text-xs text-muted-foreground">Kendaraan Aktif</div></div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold text-navy">Proyek Terbaru</h2>
            <button onClick={() => navigate("/projects")} className="text-xs text-primary flex items-center gap-1">Lihat semua <ArrowRight size={12} /></button>
          </div>
          <div className="overflow-x-auto">
            {recent.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Belum ada proyek.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3">Project ID</th><th className="pr-3">No. Polisi</th><th className="pr-3 hidden sm:table-cell">Customer</th>
                  <th className="pr-3 hidden md:table-cell">Penanggung Jawab</th><th className="pr-3">Tahap</th><th></th>
                </tr></thead>
                <tbody>
                  {recent.map((p) => (
                    <tr key={p.id} data-testid={`recent-row-${p.project_id}`} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)}>
                      <td className="py-2 pr-3 font-mono-k3 text-xs font-semibold text-primary">{p.project_id}</td>
                      <td className="pr-3 font-medium">{p.plate}</td>
                      <td className="pr-3 hidden sm:table-cell">{p.customer_name}</td>
                      <td className="pr-3 hidden md:table-cell text-xs">{p.penanggung_jawab_name || <span className="text-primary">Belum ada PJ</span>}</td>
                      <td className="pr-3"><StatusBadge status={p.stage} /></td>
                      <td><ArrowRight size={14} className="text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-primary" /> Perlu Perhatian</h2>
          {attention.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Tidak ada.</p> : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {attention.map((p) => (
                <div key={p.id} data-testid={`attention-row-${p.project_id}`} onClick={() => navigate(`/projects/${p.id}`)}
                  className="p-2.5 rounded-lg border hover:border-primary cursor-pointer bg-white">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-k3 text-xs font-semibold text-primary">{p.project_id}</span>
                    <StatusBadge status={p.stage} />
                  </div>
                  <div className="text-xs font-medium mt-0.5">{p.plate} · {p.customer_name}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.attention_reasons.map((r, i) => (
                      <span key={i} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{r}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
